import {
  alongPathCandidates,
  IAlongPathCandidate,
} from './exportLabelAlongPath'
import {
  generateLabelCandidates,
  ILabelCandidate,
} from './exportLabelCandidates'
import { clusterNearbyPins, IExportPinCluster } from './exportLabelClusters'
import { boxesIntersect, LabelGridIndex } from './exportLabelGrid'
import {
  IKeyTableCopy,
  IKeyTableLayout,
  keyRoomForSingles,
  keyTableCapacity,
  layoutKeyTable,
} from './exportLabelKeyTable'
import {
  CLUSTER_MARKER_RADIUS_PX,
  CLUSTER_MARKER_TEXT_PX,
  EXPORT_LABEL_KIND_ORDER,
  ExportLabelKind,
  haloPadPx,
  IBox,
  IExportKeyEntry,
  IExportLabelFeature,
  IExportLabelInk,
  IExportLabelPlacement,
  IExportLabelResult,
  IExportLabelScene,
  IExportLeader,
  IExportObstacle,
  IOrientedBox,
  KEY_BADGE_RADIUS_PX,
  KEY_BADGE_TEXT_PX,
  KEY_BADGE_WEIGHT,
  LABEL_FLOOR_PX,
  LabelMeasure,
  MAX_KEY_ENTRIES,
} from './exportLabelTypes'
import { displaceMarkers } from './exportMarkerDisplacement'
import { BasemapInk, LABEL_MARKER_GAP_PX } from './labelLayerStyle'

const OBSTACLE_OWNER = ''
const DEFAULT_INSET_PX = 4
const ALONG_PATH_GAP_PX = 2
const KEY_TAG_GAP_PX = 2

export interface ISolveOptions {
  floorPx?: number
  // Off, a name either fits at its full size or is numbered into the key.
  canShrink?: boolean
  insetPx?: number
  // Sizes the label key.
  keyScale?: number
  // Sizes the key's tags on the map; the key's scale unless given.
  markScale?: number
  // Sizes the group disks, which stand in for pins; the tags' scale unless given.
  diskScale?: number
  // The label key's room and copy; without it the key is off.
  key?: { layout: IKeyTableLayout; copy: IKeyTableCopy } | null
  // The tone of the ground at these page points. A bare label takes it where it was placed,
  // which need not be where the map read the label's ground.
  toneAt?: (points: Array<{ x: number; y: number }>) => BasemapInk | null
}

// Points along a label's line: its middle and halfway out to each end, turned with a label run
// along a markup.
const lineSamples = (
  placement: Pick<
    IExportLabelPlacement,
    'box' | 'anchor' | 'angleRad' | 'runWidthPx'
  >,
): Array<{ x: number; y: number }> => {
  const { box } = placement
  const centre =
    placement.angleRad != null
      ? placement.anchor
      : { x: box.x + box.width / 2, y: box.y + box.height / 2 }
  const reach = (placement.runWidthPx ?? box.width) / 4
  const angle = placement.angleRad ?? 0
  const dx = reach * Math.cos(angle)
  const dy = reach * Math.sin(angle)
  return [
    centre,
    { x: centre.x - dx, y: centre.y - dy },
    { x: centre.x + dx, y: centre.y + dy },
  ]
}

// A plate reads on any ground, so a plated label keeps its ink; a bare one takes the ground's.
const inkedWherePlaced = (
  placement: IExportLabelPlacement,
  toneAt: NonNullable<ISolveOptions['toneAt']>,
): IExportLabelPlacement => {
  if (placement.keyTag != null || placement.clusterCount != null) {
    return placement
  }
  if (placement.ink?.isPlated) return placement
  const inkOn = (
    samples: Array<{ x: number; y: number }>,
  ): IExportLabelInk | undefined => {
    const tone = toneAt(samples)
    return tone ? { isPlated: false, color: null, tone } : undefined
  }
  const ink = inkOn(lineSamples(placement))
  return {
    ...placement,
    ...(ink && { ink }),
    // Each line reads its own ground: what a stack holds can run onto ground its name does not.
    ...(placement.lines && {
      lines: placement.lines.map(line => {
        const lineInk = inkOn(
          lineSamples({ box: line.box, anchor: placement.anchor }),
        )
        return lineInk ? { ...line, ink: lineInk } : line
      }),
    }),
  }
}

const isOnPage = (
  { x, y }: { x: number; y: number },
  scene: IExportLabelScene,
): boolean => x >= 0 && y >= 0 && x <= scene.width && y <= scene.height

const pageBox = (scene: IExportLabelScene): IBox => ({
  x: 0,
  y: 0,
  width: scene.width,
  height: scene.height,
})

const pathBounds = (path: Array<{ x: number; y: number }>): IBox => {
  const xs = path.map(point => point.x)
  const ys = path.map(point => point.y)
  const x = Math.min(...xs)
  const y = Math.min(...ys)
  return { x, y, width: Math.max(...xs) - x, height: Math.max(...ys) - y }
}

// A pin whose icon reaches the page is on the sheet, wherever its point is.
const isOnSheet = (
  feature: IExportLabelFeature,
  scene: IExportLabelScene,
): boolean =>
  feature.icon
    ? boxesIntersect(feature.icon, pageBox(scene))
    : isOnPage(feature.anchor, scene)

const isInsideInset = (
  box: IBox,
  scene: IExportLabelScene,
  inset: number,
): boolean =>
  box.x >= inset &&
  box.y >= inset &&
  box.x + box.width <= scene.width - inset &&
  box.y + box.height <= scene.height - inset

const compareReadingOrder = (
  a: IExportLabelFeature,
  b: IExportLabelFeature,
): number =>
  EXPORT_LABEL_KIND_ORDER.indexOf(a.kind) -
    EXPORT_LABEL_KIND_ORDER.indexOf(b.kind) ||
  a.anchor.y - b.anchor.y ||
  a.anchor.x - b.anchor.x ||
  a.id.localeCompare(b.id)

const placementOf = (
  feature: IExportLabelFeature,
  candidate: Pick<
    ILabelCandidate,
    'sizePx' | 'lineHeightPx' | 'box' | 'align' | 'lines'
  >,
): IExportLabelPlacement => ({
  id: feature.id,
  kind: feature.kind,
  text: feature.text,
  sizePx: candidate.sizePx,
  lineHeightPx: candidate.lineHeightPx,
  weight: feature.weight,
  letterSpacingEm: feature.letterSpacingEm,
  box: candidate.box,
  align: candidate.align,
  anchor: feature.anchor,
  ink: feature.ink,
  lines: candidate.lines,
})

interface ISymbolBox {
  owner: string
  box: IBox
}

// The empty space between two boxes; 0 where they touch or overlap.
const boxGap = (a: IBox, b: IBox): number =>
  Math.hypot(
    Math.max(0, a.x - (b.x + b.width), b.x - (a.x + a.width)),
    Math.max(0, a.y - (b.y + b.height), b.y - (a.y + a.height)),
  )

const grown = (box: IBox, by: number): IBox => ({
  x: box.x - by,
  y: box.y - by,
  width: box.width + 2 * by,
  height: box.height + 2 * by,
})

// A label reads as naming the symbol nearest to it, so a spot closer to another symbol than to its
// own comes after every other, shrunk ones too (Imhof's association rule). The symbols that could
// be nearer than the feature's own icon are those within its candidates' reach.
function associated(
  feature: IExportLabelFeature,
  candidates: ILabelCandidate[],
  symbols: ISymbolBox[],
): ILabelCandidate[] {
  const own = feature.icon
  if (!own || !candidates.length) return candidates
  const reach = grown(
    candidates.reduce((union, { box }) => {
      const x = Math.min(union.x, box.x)
      const y = Math.min(union.y, box.y)
      return {
        x,
        y,
        width: Math.max(union.x + union.width, box.x + box.width) - x,
        height: Math.max(union.y + union.height, box.y + box.height) - y,
      }
    }, own),
    feature.sizePx,
  )
  const others = symbols.filter(
    symbol =>
      symbol.owner !== feature.id &&
      symbol.box !== own &&
      boxesIntersect(symbol.box, reach),
  )
  if (!others.length) return candidates
  const isMisattributed = candidates.map(candidate => {
    const ownGap = boxGap(candidate.box, own)
    return others.some(other => boxGap(candidate.box, other.box) < ownGap)
  })
  return [
    ...candidates.filter((_, i) => !isMisattributed[i]),
    ...candidates.filter((_, i) => isMisattributed[i]),
  ]
}

const squareAt = (centre: { x: number; y: number }, radius: number): IBox => ({
  x: centre.x - radius,
  y: centre.y - radius,
  width: 2 * radius,
  height: 2 * radius,
})

const clampInsideInset = (
  box: IBox,
  scene: IExportLabelScene,
  inset: number,
): IBox => ({
  ...box,
  x: Math.min(Math.max(box.x, inset), scene.width - inset - box.width),
  y: Math.min(Math.max(box.y, inset), scene.height - inset - box.height),
})

// The icon's corners, then spots just clear of it: beside the head right then left, above it, below
// the tip. A stack's upper-left corner wears its count chip.
const iconTagSpots = (
  icon: IBox,
  clear: number,
  hasChip: boolean,
): Array<{ x: number; y: number }> => {
  const headY = icon.y + icon.width / 2
  const middleX = icon.x + icon.width / 2
  return [
    { x: icon.x + icon.width, y: icon.y },
    ...(hasChip ? [] : [{ x: icon.x, y: icon.y }]),
    { x: icon.x + icon.width, y: icon.y + icon.height },
    { x: icon.x, y: icon.y + icon.height },
    { x: icon.x + icon.width + clear, y: headY },
    { x: icon.x - clear, y: headY },
    { x: middleX, y: icon.y - clear },
    { x: middleX, y: icon.y + icon.height + clear },
  ]
}

// A tag takes the first free spot by the icon, or the anchor of a Location with none. With every
// spot taken it is forced onto the first, the icon's top-right corner, inside the inset: over
// whatever is there, and clear of the glyph unless the inset pushes it inward.
function tagBox(
  feature: IExportLabelFeature,
  scene: IExportLabelScene,
  inset: number,
  grid: LabelGridIndex,
  markScale: number,
): { box: IBox; isForced: boolean } {
  const radius = KEY_BADGE_RADIUS_PX * markScale
  const clear = radius + KEY_TAG_GAP_PX * markScale
  const { icon } = feature
  const spots = icon
    ? iconTagSpots(icon, clear, (feature.stackCount ?? 0) > 1)
    : [feature.anchor]
  const boxes = spots.map(spot => squareAt(spot, radius))
  const free = boxes.find(
    box => isInsideInset(box, scene, inset) && !grid.hitsBox(box, feature.id),
  )
  if (free) {
    return { box: free, isForced: false }
  }
  return { box: clampInsideInset(boxes[0], scene, inset), isForced: true }
}

// Where a group's letter sits on its disk's rim: at 45°, up and to the right.
export const rimSpot = (
  centre: { x: number; y: number },
  radius: number,
): { x: number; y: number } => ({
  x: centre.x + Math.SQRT1_2 * radius,
  y: centre.y - Math.SQRT1_2 * radius,
})

// A group's letter sits on the disk's rim at 45°, at the first diagonal inside the inset.
function groupTagBox(
  centre: { x: number; y: number },
  diskRadius: number,
  scene: IExportLabelScene,
  inset: number,
  markScale: number,
): IBox {
  const offset = Math.SQRT1_2 * diskRadius
  const boxes = [
    rimSpot(centre, diskRadius),
    { x: centre.x - offset, y: centre.y - offset },
    { x: centre.x + offset, y: centre.y + offset },
    { x: centre.x - offset, y: centre.y + offset },
  ].map(spot => squareAt(spot, KEY_BADGE_RADIUS_PX * markScale))
  return (
    boxes.find(box => isInsideInset(box, scene, inset)) ??
    clampInsideInset(boxes[0], scene, inset)
  )
}

export const keyTagPlacement = (
  id: string,
  kind: ExportLabelKind,
  tag: string,
  box: IBox,
  anchor: { x: number; y: number },
  markScale: number,
): IExportLabelPlacement => ({
  id,
  kind,
  text: tag,
  sizePx: KEY_BADGE_TEXT_PX * markScale,
  lineHeightPx: KEY_BADGE_TEXT_PX * markScale,
  weight: KEY_BADGE_WEIGHT,
  letterSpacingEm: 0,
  box,
  align: 'middle',
  keyTag: tag,
  anchor,
})

// A counter disk in box: the cluster's own symbol, also the page band's legend sample.
export const clusterPlacement = (
  id: string,
  box: IBox,
  count: number,
  diskScale: number,
  text = String(count),
): IExportLabelPlacement => {
  const radius = CLUSTER_MARKER_RADIUS_PX * diskScale
  return {
    id,
    kind: 'workflowPin',
    text,
    sizePx: CLUSTER_MARKER_TEXT_PX * diskScale,
    lineHeightPx: CLUSTER_MARKER_TEXT_PX * diskScale,
    weight: KEY_BADGE_WEIGHT,
    letterSpacingEm: 0,
    box,
    align: 'middle',
    clusterCount: count,
    anchor: { x: box.x + radius, y: box.y + radius },
  }
}

// The cluster stands in for its pins: one counter disk at their centre, held inside the inset.
const clusterMarker = (
  cluster: IExportPinCluster,
  scene: IExportLabelScene,
  inset: number,
  diskScale: number,
): IExportLabelPlacement =>
  clusterPlacement(
    cluster.id,
    clampInsideInset(
      squareAt(cluster.centre, CLUSTER_MARKER_RADIUS_PX * diskScale),
      scene,
      inset,
    ),
    cluster.count,
    diskScale,
  )

// A, B … Z, then AA, AB …
const groupLetter = (index: number): string => {
  let letters = ''
  for (let n = index + 1; n > 0; n = Math.floor((n - 1) / 26)) {
    letters = String.fromCharCode(65 + ((n - 1) % 26)) + letters
  }
  return letters
}

const boxAt = (box: IBox, centre: { x: number; y: number }): IBox => ({
  x: centre.x - box.width / 2,
  y: centre.y - box.height / 2,
  width: box.width,
  height: box.height,
})

// Location icons that would overlap are moved apart before any label is placed, so each label then
// stands beside the icon where it is actually drawn.
function displaceLocationIcons(
  features: IExportLabelFeature[],
  scene: IExportLabelScene,
  fixed: IBox[],
): {
  singles: IExportLabelFeature[]
  obstacles: IExportObstacle[]
  leaders: IExportLeader[]
  movedMarkers: IExportLabelResult['movedMarkers']
} {
  const movable = [
    ...features
      .filter(f => f.kind === 'location' && f.icon && f.markerKey)
      .map(f => ({ key: f.markerKey, icon: f.icon })),
    // Like a labelled Location, an unlabelled icon counts only where it reaches the page: pairing
    // every icon of a large site against every other made each solve quadratic in the site.
    ...(scene.markers ?? []).filter(m =>
      boxesIntersect(m.icon, pageBox(scene)),
    ),
  ]
  const moved = displaceMarkers(movable, fixed)
  if (!moved.size) {
    return {
      singles: features,
      obstacles: scene.obstacles,
      leaders: [],
      movedMarkers: [],
    }
  }
  const replaced = new Map<IBox, IBox>()
  const leaders: IExportLeader[] = []
  movable.forEach(({ key, icon }) => {
    const to = moved.get(key)
    if (!to) return
    replaced.set(icon, boxAt(icon, to))
    const from = { x: icon.x + icon.width / 2, y: icon.y + icon.height / 2 }
    const distance = Math.hypot(to.x - from.x, to.y - from.y)
    const radius = Math.max(icon.width, icon.height) / 2
    // Still under its own icon, the true point needs no leader to be found.
    if (distance <= radius) return
    const edge = (distance - radius) / distance
    leaders.push({
      key,
      from,
      to: {
        x: from.x + (to.x - from.x) * edge,
        y: from.y + (to.y - from.y) * edge,
      },
    })
  })
  return {
    singles: features.map(f => {
      const icon = f.icon && replaced.get(f.icon)
      if (!icon) return f
      return {
        ...f,
        icon,
        anchor: {
          x: f.anchor.x + icon.x - f.icon.x,
          y: f.anchor.y + icon.y - f.icon.y,
        },
      }
    }),
    obstacles: scene.obstacles.map(box => replaced.get(box) ?? box),
    leaders,
    movedMarkers: [...moved].map(([key, centre]) => ({ key, centre })),
  }
}

const compareImportance = (
  a: IExportLabelFeature,
  b: IExportLabelFeature,
): number =>
  a.priority - b.priority || a.sortKey - b.sortKey || a.id.localeCompare(b.id)

// Last, and only into room nothing else wanted: a markup's label is a secondary annotation, so
// where none of its segments has space it is left off, never keyed, only counted.
function placeAlongPaths(
  features: IExportLabelFeature[],
  scene: IExportLabelScene,
  grid: LabelGridIndex,
  inset: number,
  measure: LabelMeasure,
): { placements: IExportLabelPlacement[]; leftOff: number } {
  const placements = [...features].sort(compareImportance).flatMap(feature => {
    const runWidthPx =
      measure(
        feature.text,
        feature.sizePx,
        feature.weight,
        feature.letterSpacingEm,
      ) +
      2 * haloPadPx(feature.sizePx)
    // The run collides as the turned rectangle it is drawn in, not as the upright box around it.
    const shapeOf = (candidate: IAlongPathCandidate): IOrientedBox => ({
      centre: candidate.centre,
      width: runWidthPx,
      height: feature.lineHeightPx,
      angleRad: candidate.angleRad,
    })
    const chosen = alongPathCandidates(
      feature.path,
      !!feature.isClosed,
      { width: runWidthPx, height: feature.lineHeightPx },
      ALONG_PATH_GAP_PX,
    ).find(
      c =>
        isInsideInset(c.box, scene, inset) &&
        !grid.hitsBox(c.box, undefined, shapeOf(c)),
    )
    if (!chosen) {
      return []
    }
    grid.insert(chosen.box, feature.id, shapeOf(chosen))
    return [
      {
        ...placementOf(feature, {
          box: chosen.box,
          align: 'middle',
          sizePx: feature.sizePx,
          lineHeightPx: feature.lineHeightPx,
        }),
        anchor: chosen.centre,
        angleRad: chosen.angleRad,
        runWidthPx,
      },
    ]
  })
  return { placements, leftOff: features.length - placements.length }
}

// Touching pins become one counter disk first, lettered into the key with its names. Then greedy,
// most constrained first: a feature with few free candidates claims one before a roomier neighbour
// takes it. Whatever finds no room is numbered into the key, up to MAX_KEY_ENTRIES. Tags stop where
// the key's room does, so every tag on the map has its row: a group past it keeps its disk
// unlettered, and the key's last row counts the rest, so every name on the sheet is on the map or
// reached from it.
export function solveExportLabels(
  scene: IExportLabelScene,
  measure: LabelMeasure,
  opts: ISolveOptions = {},
): IExportLabelResult {
  const floorPx = opts.floorPx ?? LABEL_FLOOR_PX
  const inset = opts.insetPx ?? DEFAULT_INSET_PX
  const keyScale = opts.keyScale ?? 1
  const markScale = opts.markScale ?? keyScale
  const diskScale = opts.diskScale ?? markScale
  const isKeyed = !!opts.key
  const keyCapacity = opts.key ? keyTableCapacity(opts.key.layout, keyScale) : 0
  const page = pageBox(scene)
  const alongPath = scene.features.filter(
    f => f.path && boxesIntersect(pathBounds(f.path), page),
  )
  const clustering = clusterNearbyPins(
    scene.features.filter(f => !f.path && isOnSheet(f, scene)),
    (scene.pins ?? []).filter(pin => boxesIntersect(pin.icon, page)),
    markScale,
    diskScale,
  )
  const { clusters } = clustering
  // A clustered pin is not drawn, so it neither blocks a label nor keeps one of its own.
  const hiddenIcons = new Set(clusters.flatMap(c => c.icons))
  const placements: IExportLabelPlacement[] = []
  const groupEntries: IExportKeyEntry[] = []
  let unletteredNames = 0
  clusters
    .map(cluster => ({
      cluster,
      disk: clusterMarker(cluster, scene, inset, diskScale),
    }))
    .sort(
      (a, b) =>
        a.disk.anchor.y - b.disk.anchor.y ||
        a.disk.anchor.x - b.disk.anchor.x ||
        a.disk.id.localeCompare(b.disk.id),
    )
    .forEach(({ cluster, disk }) => {
      placements.push(disk)
      if (!isKeyed || !cluster.members.length) return
      if (keyRoomForSingles(keyCapacity, groupEntries.length + 1) < 0) {
        unletteredNames += cluster.members.length
        return
      }
      const tag = groupLetter(groupEntries.length)
      const box = groupTagBox(
        disk.anchor,
        disk.box.width / 2,
        scene,
        inset,
        markScale,
      )
      placements.push(
        keyTagPlacement(
          `${disk.id}:tag`,
          'workflowPin',
          tag,
          box,
          disk.anchor,
          markScale,
        ),
      )
      groupEntries.push({
        tag,
        count: cluster.count,
        members: cluster.members.map(member => member.text),
      })
    })
  const { singles, obstacles, leaders, movedMarkers } = displaceLocationIcons(
    clustering.singles,
    scene,
    [
      ...placements.map(p => p.box),
      ...clustering.singles
        .filter(f => f.kind === 'workflowPin' && f.icon)
        .map(f => f.icon),
      ...(scene.pins ?? [])
        .map(p => p.icon)
        .filter(icon => !hiddenIcons.has(icon)),
    ],
  )
  const grid = new LabelGridIndex(scene.width, scene.height)
  obstacles
    .filter(box => !hiddenIcons.has(box))
    .forEach(box => grid.insert(box, OBSTACLE_OWNER, box.shape))
  singles.forEach(f => f.icon && grid.insert(f.icon, f.id))
  placements.forEach(p => grid.insert(p.box, OBSTACLE_OWNER))
  const movedTo = new Map(movedMarkers.map(({ key, centre }) => [key, centre]))
  const symbols: ISymbolBox[] = [
    ...singles.filter(f => f.icon).map(f => ({ owner: f.id, box: f.icon })),
    ...placements
      .filter(p => p.clusterCount != null)
      .map(p => ({ owner: p.id, box: p.box })),
    ...(scene.pins ?? [])
      .filter(pin => !hiddenIcons.has(pin.icon))
      .map(pin => ({ owner: pin.key, box: pin.icon })),
    ...(scene.markers ?? []).map(marker => {
      const centre = movedTo.get(marker.key)
      return {
        owner: marker.key,
        box: centre ? boxAt(marker.icon, centre) : marker.icon,
      }
    }),
  ]

  const queue = singles
    .map(feature => {
      const candidates = associated(
        feature,
        generateLabelCandidates(feature, measure, {
          floorPx,
          gapPx: LABEL_MARKER_GAP_PX,
          canShrink: opts.canShrink ?? true,
        }).filter(c => isInsideInset(c.box, scene, inset)),
        symbols,
      )
      // One line only, so a stack's two-line spots do not count it twice as roomy.
      const freeCount = candidates.filter(
        c => !c.lines && !grid.hitsBox(c.box, feature.id),
      ).length
      return { feature, candidates, freeCount }
    })
    .sort(
      (a, b) =>
        a.feature.priority - b.feature.priority ||
        a.freeCount - b.freeCount ||
        a.feature.sortKey - b.feature.sortKey ||
        a.feature.id.localeCompare(b.feature.id),
    )

  const unplaced: IExportLabelFeature[] = []
  let shrunk = 0
  queue.forEach(({ feature, candidates }) => {
    const chosen = candidates.find(c => !grid.hitsBox(c.box, feature.id))
    if (!chosen) {
      unplaced.push(feature)
      return
    }
    grid.insert(chosen.box, feature.id)
    placements.push(placementOf(feature, chosen))
    if (chosen.isShrunk) shrunk += 1
  })

  // The most important get a tag first, so they take the free corners; numbers then follow reading
  // order. Tags are capped in entries, the key's room in rows, where a numbered stack lists what it
  // holds under its name. Once one does not fit, none after it is numbered, so a smaller name never
  // takes a number from a more important one.
  const roomRows = keyRoomForSingles(keyCapacity, groupEntries.length)
  let overCap = 0
  let forcedTags = 0
  const tagged: Array<{ feature: IExportLabelFeature; box: IBox }> = []
  let keyRows = 0
  if (isKeyed) {
    ;[...unplaced].sort(compareImportance).forEach(feature => {
      const rows = 1 + (feature.stack?.details.length ?? 0)
      if (
        overCap ||
        tagged.length >= MAX_KEY_ENTRIES ||
        keyRows + rows > roomRows
      ) {
        overCap += 1
        return
      }
      keyRows += rows
      const { box, isForced } = tagBox(feature, scene, inset, grid, markScale)
      if (isForced) forcedTags += 1
      grid.insert(box, feature.id)
      tagged.push({ feature, box })
    })
    tagged.sort((a, b) => compareReadingOrder(a.feature, b.feature))
  }
  const singleEntries: IExportKeyEntry[] = tagged.map(
    ({ feature, box }, index) => {
      const tag = String(index + 1)
      placements.push(
        keyTagPlacement(
          feature.id,
          feature.kind,
          tag,
          box,
          feature.anchor,
          markScale,
        ),
      )
      return feature.stack
        ? { tag, text: feature.stack.header, details: feature.stack.details }
        : { tag, text: feature.text }
    },
  )
  const markups = placeAlongPaths(alongPath, scene, grid, inset, measure)
  placements.push(...markups.placements)

  const key = [...singleEntries, ...groupEntries]
  const { table, trimmed } = opts.key
    ? layoutKeyTable(
        key,
        overCap + unletteredNames,
        opts.key.layout,
        opts.key.copy,
        measure,
        keyScale,
      )
    : { table: null, trimmed: 0 }
  const groupNames = clusters.reduce((sum, c) => sum + c.members.length, 0)
  return {
    placements: opts.toneAt
      ? placements.map(p => inkedWherePlaced(p, opts.toneAt))
      : placements,
    key,
    keyTable: table,
    hiddenPinKeys: clusters.flatMap(c => c.pinKeys),
    leaders,
    movedMarkers,
    stats: {
      names: queue.length + groupNames,
      onMap: queue.length - unplaced.length,
      shrunk,
      numbered: singleEntries.length,
      groups: clusters.length,
      groupNames,
      listedMembers: isKeyed ? groupNames - unletteredNames - trimmed : 0,
      overflow: overCap + unletteredNames + trimmed,
      leftOff: isKeyed ? 0 : unplaced.length,
      markupShown: markups.placements.length,
      markupLeftOff: markups.leftOff,
      forcedTags,
    },
  }
}
